import { apiEnvelopeSchema, REQUEST_ID_HEADER } from '@hvac/contracts';
import type { z } from 'zod';
import { env } from '../../config/env.js';
import { ApiError, NetworkError } from './errors.js';

/**
 * The typed API client.
 *
 * Three properties every call inherits:
 *
 *  1. **The response is validated.** Each request declares the schema of the
 *     payload it expects, and the response is parsed against it. A server
 *     change that breaks the contract surfaces here, once, with a clear
 *     message, instead of as `undefined` rendering inside a table three
 *     components away.
 *  2. **Errors are typed.** Failures become `ApiError` with a machine code and
 *     a request id, never a bare string.
 *  3. **Auth is automatic.** The bearer token is attached by a supplied
 *     provider, so no feature has to remember it.
 */

export type TokenProvider = () => Promise<string | null>;

export interface ApiClientOptions {
  readonly baseUrl: string;
  readonly getAccessToken: TokenProvider;
  /** Injected in tests. Defaults to the platform `fetch`. */
  readonly fetchImpl?: typeof fetch;
  readonly timeoutMs?: number;
}

export interface RequestOptions<TSchema extends z.ZodType> {
  readonly method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  readonly path: string;
  readonly schema: TSchema;
  readonly body?: unknown;
  readonly signal?: AbortSignal;
}

export interface ApiClient {
  request<TSchema extends z.ZodType>(options: RequestOptions<TSchema>): Promise<z.infer<TSchema>>;
}

const DEFAULT_TIMEOUT_MS = 15_000;

export function createApiClient(options: ApiClientOptions): ApiClient {
  const fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  return {
    async request<TSchema extends z.ZodType>(
      request: RequestOptions<TSchema>,
    ): Promise<z.infer<TSchema>> {
      const token = await options.getAccessToken();

      const headers = new Headers({ Accept: 'application/json' });
      if (token !== null) headers.set('Authorization', `Bearer ${token}`);
      if (request.body !== undefined) headers.set('Content-Type', 'application/json');

      // A request that never settles blocks a loading spinner forever. Every
      // call is bounded, and an explicit caller signal composes with the bound.
      const timeoutSignal = AbortSignal.timeout(timeoutMs);
      const signal =
        request.signal === undefined
          ? timeoutSignal
          : AbortSignal.any([request.signal, timeoutSignal]);

      let response: Response;
      try {
        response = await fetchImpl(`${options.baseUrl}${request.path}`, {
          method: request.method ?? 'GET',
          headers,
          signal,
          ...(request.body === undefined ? {} : { body: JSON.stringify(request.body) }),
        });
      } catch (cause) {
        throw new NetworkError('The request could not be completed.', { cause });
      }

      const requestId = response.headers.get(REQUEST_ID_HEADER) ?? 'unknown';

      // 204 carries no body, so there is nothing to parse.
      if (response.status === 204) {
        return undefined as z.infer<TSchema>;
      }

      let payload: unknown;
      try {
        payload = await response.json();
      } catch (cause) {
        throw new NetworkError(`The server returned a non-JSON response (${response.status}).`, {
          cause,
        });
      }

      const envelope = apiEnvelopeSchema.safeParse(payload);
      if (!envelope.success) {
        throw new NetworkError(
          `The server returned a malformed envelope from ${request.path} (reference ${requestId}).`,
          { cause: envelope.error },
        );
      }

      // The envelope's `ok` flag is authoritative rather than the HTTP status,
      // because a few endpoints legitimately return a successful payload with a
      // non-2xx status, readiness reports 503 while still describing which
      // dependency is down.
      if (!envelope.data.ok) {
        throw new ApiError({
          body: envelope.data.error,
          httpStatus: response.status,
          requestId: envelope.data.meta.requestId,
        });
      }

      const parsed = request.schema.safeParse(envelope.data.data);
      if (!parsed.success) {
        // The server and this client disagree about the contract. Surfacing it
        // as a network-level fault is honest: no amount of retrying by the user
        // will help, and it needs an engineer.
        throw new NetworkError(
          `The response from ${request.path} did not match the expected contract (reference ${requestId}).`,
          { cause: parsed.error },
        );
      }

      return parsed.data;
    },
  };
}

/** Base URL for the deployed API, from validated configuration. */
export const API_BASE_URL = env.VITE_API_BASE_URL.replace(/\/+$/, '');
